import { RISK_LEVEL_COLORS } from "@app/theme/tokens";
import { Box, Divider, Typography } from "@mui/material";
import type { SpofCard } from "../types";
import { sar } from "../utils/format";
import DashboardCard from "./DashboardCard";
import { PendingValue } from "./PendingValue";

function Score({ label, value }: { label: string; value: number | null }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      {value === null ? (
        <PendingValue />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

export default function SpofRiskAssessmentCard({ spof }: { spof: SpofCard }) {
  const color = spof.risk_category
    ? RISK_LEVEL_COLORS[spof.risk_category]
    : "text.primary";
  const quoted = spof.mitigations
    .map((mitigation) => mitigation.total_cost_sar)
    .filter((cost) => cost !== null);
  const totalCost = quoted.length
    ? quoted.reduce((sum, cost) => sum + cost, 0)
    : null;

  return (
    <DashboardCard title="Risk Assessment">
      <Score label="Severity" value={spof.severity} />
      <Score label="Likelihood" value={spof.likelihood} />
      <Score label="Detectability" value={spof.detection_difficulty} />
      <Score label="Plant Life" value={spof.plant_phase_life} />

      <Divider sx={{ my: 1.5 }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Final Score
        </Typography>
        {spof.final_risk_score === null ? (
          <PendingValue />
        ) : (
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color }}>
            {spof.final_risk_score}
          </Typography>
        )}
        <Divider orientation="vertical" flexItem />
        <Typography sx={{ fontWeight: 700, color, textTransform: "uppercase" }}>
          {spof.risk_category ?? <PendingValue />}
        </Typography>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Cost of the whole mitigation plan, so the risk reads next to what removing it costs. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Total Cost
        </Typography>
        {totalCost === null ? (
          <PendingValue />
        ) : (
          <Typography sx={{ fontWeight: 700 }}>{sar(totalCost)}</Typography>
        )}
      </Box>
    </DashboardCard>
  );
}
